import { Toaster } from "@/shared/components/ui/toaster";
import { Toaster as Sonner } from "@/shared/components/ui/sonner";
import { TooltipProvider } from "@/shared/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/features/auth/AuthContext";
import { ErrorBoundary, type ErrorInfo } from "@/shared/components/ErrorBoundary";
import { RouteErrorBoundary } from "@/shared/components/RouteErrorBoundary";
import ProtectedRoute from "@/shared/components/ProtectedRoute";
import Layout from "@/shared/components/Layout";
import { Suspense, lazy } from "react";
import { AppWrapper } from "@/shared/components/AppWrapper";
import { ThemeProvider } from "next-themes";
import { UserPreferencesProvider } from "@/shared/hooks/useUserPreferences";
import { ThemeSyncProvider } from "@/shared/components/ThemeSyncProvider";

import Login from "@/features/auth/pages/Login";
import ForgotPassword from "@/features/auth/pages/ForgotPassword";
import ResetPassword from "@/features/auth/pages/ResetPassword";
import SetPassword from "@/features/auth/pages/SetPassword";
import MfaChallengePage from "@/features/mfa/pages/MfaChallengePage";
import MfaSetupPage from "@/features/mfa/pages/MfaSetupPage";
import NotFound from "@/app/NotFound";

const Index = lazy(() => import("@/app/Index"));
const AccountsList = lazy(() => import("@/features/accounts/pages/AccountsList"));
const AccountDetail = lazy(() => import("@/features/accounts/pages/AccountDetail"));
const MonitoredDevices = lazy(() => import("@/features/devices/pages/MonitoredDevices"));
const DeviceDetails = lazy(() => import("@/features/devices/pages/DeviceDetails"));
const DeviceSoftware = lazy(() => import("@/features/devices/pages/DeviceSoftware"));
const DeviceConnectedDevices = lazy(() => import("@/features/devices/pages/DeviceConnectedDevices"));
const DeviceProcesses = lazy(() => import("@/features/devices/pages/DeviceProcesses"));
const LocalUsers = lazy(() => import("@/features/devices/pages/LocalUsers"));
const DeviceAlerts = lazy(() => import("@/features/devices/pages/DeviceAlerts"));
const DeviceWindowsUpdates = lazy(() => import("@/features/devices/pages/DeviceWindowsUpdates"));
const DeviceUserActivities = lazy(() => import("@/features/devices/pages/DeviceUserActivities"));
const DeviceCommandsHistory = lazy(() => import("@/features/devices/pages/DeviceCommandsHistory"));
const UserRegistration = lazy(() => import("@/features/users/pages/UserRegistration"));
const UserEdit = lazy(() => import("@/features/users/pages/UserEdit"));
const AccessControl = lazy(() => import("@/features/access-control/pages/AccessControl"));
const AlertsList = lazy(() => import("@/features/alerts/pages/AlertsList"));
const AlertsDashboard = lazy(() => import("@/features/alerts/pages/AlertsDashboard"));
const AlertRulesList = lazy(() => import("@/features/alerts/pages/AlertRulesList"));
const AlertRuleForm = lazy(() => import("@/features/alerts/pages/AlertRuleForm"));
const Settings = lazy(() => import("@/features/settings/pages/Settings"));
const InstallationTokens = lazy(() => import("@/features/installation-tokens/pages/InstallationTokens"));
const ReportsHub = lazy(() => import("@/features/reports/pages/ReportsHub"));
const DevicesReport = lazy(() => import("@/features/reports/pages/DevicesReport"));
const SoftwareReport = lazy(() => import("@/features/reports/pages/SoftwareReport"));
const UserSessionsReport = lazy(() => import("@/features/reports/pages/UserSessionsReport"));
const UserActivityByDeviceReport = lazy(() => import("@/features/reports/pages/UserActivityByDeviceReport"));
const EventsReport = lazy(() => import("@/features/reports/pages/EventsReport"));
const EventHistoryReport = lazy(() => import("@/features/reports/pages/EventHistoryReport"));
const GeolocationReport = lazy(() => import("@/features/reports/pages/GeolocationReport"));
const GeolocationDeviceHistory = lazy(() => import("@/features/reports/pages/GeolocationDeviceHistory"));
const GlobalSearch = lazy(() => import("@/features/search/pages/SearchResultsPage"));
const PlatformOverview = lazy(() => import("@/features/administration/pages/PlatformOverview"));
const TenantManagement = lazy(() => import("@/features/administration/pages/TenantManagement"));
const TenantDetail = lazy(() => import("@/features/administration/pages/TenantDetail"));
const DeviceGroups = lazy(() => import("@/features/device-groups/pages/DeviceGroups"));

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center space-y-4">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
      <p className="text-muted-foreground">Carregando...</p>
    </div>
  </div>
);

type PermissionCheck =
  | 'isAdmin'
  | 'isOperador'
  | 'isVisualizador'
  | 'isTenant'
  | 'isStandAlone'
  | 'isPlatformAdmin';

const createProtectedRoute = (
  routeName: string,
  Component: React.LazyExoticComponent<React.ComponentType<any>>,
  requiredChecks?: PermissionCheck[],
  withLayout: boolean = true
) => (
  <RouteErrorBoundary routeName={routeName}>
    <ProtectedRoute requiredChecks={requiredChecks ?? []}>
      <Suspense fallback={<LoadingFallback />}>
        {withLayout ? (
          <Layout>
            <Component />
          </Layout>
        ) : (
          <Component />
        )}
      </Suspense>
    </ProtectedRoute>
  </RouteErrorBoundary>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof Error && 'status' in error) {
          const status = (error as Error & { status?: number }).status;
          if (status !== undefined && status >= 400 && status < 500) {
            return false;
          }
        }
        return failureCount < 3;
      },
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
});

const handleAppError = (error: Error, errorInfo: ErrorInfo) => {
  import('@/shared/lib/error-handler').then(({ errorHandler }) => {
    errorHandler.handleError(error, { errorBoundary: true, ...errorInfo });
  });
};

const App = () => (
  <AppWrapper>
    <ErrorBoundary level="app" onError={handleAppError}>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem={true}
        storageKey="app-theme"
        disableTransitionOnChange
      >
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary level="app" onError={handleAppError}>
            <AuthProvider>
              <UserPreferencesProvider>
                <ThemeSyncProvider>
                  <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route
                    path="/login"
                    element={
                      <RouteErrorBoundary routeName="login">
                        <Login />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route
                    path="/forgot-password"
                    element={
                      <RouteErrorBoundary routeName="forgot-password">
                        <ForgotPassword />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route
                    path="/reset-password"
                    element={
                      <RouteErrorBoundary routeName="reset-password">
                        <ResetPassword />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route
                    path="/set-password"
                    element={
                      <RouteErrorBoundary routeName="set-password">
                        <SetPassword />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route
                    path="/login/mfa"
                    element={
                      <RouteErrorBoundary routeName="mfa-challenge">
                        <MfaChallengePage />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route
                    path="/mfa/setup"
                    element={
                      <RouteErrorBoundary routeName="mfa-setup">
                        <MfaSetupPage />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route path="/" element={createProtectedRoute("dashboard", Index, undefined, false)} />
                  <Route path="/dashboard" element={createProtectedRoute("dashboard", Index, undefined, false)} />
                  <Route path="/contas" element={createProtectedRoute("accounts-list", AccountsList, ['isTenant'])} />
                  <Route path="/contas/:id" element={createProtectedRoute("account-detail", AccountDetail, ['isTenant'])} />
                  <Route path="/clientes" element={<Navigate to="/contas" replace />} />
                  <Route path="/clientes/*" element={<Navigate to="/contas" replace />} />
                  <Route path="/devices" element={createProtectedRoute("devices-list", MonitoredDevices)} />
                  <Route path="/devices/:id" element={createProtectedRoute("device-details", DeviceDetails)} />
                  <Route path="/devices/:id/software" element={createProtectedRoute("device-software", DeviceSoftware)} />
                  <Route path="/devices/:id/connected-devices" element={createProtectedRoute("device-connected", DeviceConnectedDevices)} />
                  <Route path="/devices/:id/processes" element={createProtectedRoute("device-processes", DeviceProcesses)} />
                  <Route path="/devices/:id/local-users" element={createProtectedRoute("local-users", LocalUsers)} />
                  <Route path="/devices/:id/user-activities" element={createProtectedRoute("device-user-activities", DeviceUserActivities)} />
                  <Route path="/devices/:id/alerts" element={createProtectedRoute("device-alerts", DeviceAlerts)} />
                  <Route path="/devices/:id/windows-updates" element={createProtectedRoute("device-windows-updates", DeviceWindowsUpdates)} />
                  <Route path="/devices/:id/comandos" element={createProtectedRoute("device-commands", DeviceCommandsHistory)} />
                  <Route path="/controle-de-acesso" element={createProtectedRoute("access-control", AccessControl, ['isAdmin'])} />
                  <Route path="/administracao/evolucao" element={createProtectedRoute("admin-overview", PlatformOverview, ['isPlatformAdmin'])} />
                  <Route path="/administracao/tenants" element={createProtectedRoute("admin-tenants", TenantManagement, ['isPlatformAdmin'])} />
                  <Route path="/administracao/tenants/:id" element={createProtectedRoute("admin-tenant-detail", TenantDetail, ['isPlatformAdmin'])} />
                  <Route path="/usuarios/novo" element={createProtectedRoute("user-registration", UserRegistration, ['isAdmin'])} />
                  <Route path="/usuarios/:id/editar" element={createProtectedRoute("user-edit", UserEdit, ['isAdmin'])} />
                  <Route path="/alertas" element={createProtectedRoute("alerts-list", AlertsList)} />
                  <Route path="/alertas/dashboard" element={createProtectedRoute("alerts-dashboard", AlertsDashboard)} />
                  <Route path="/alertas/regras" element={createProtectedRoute("alert-rules-list", AlertRulesList)} />
                  <Route path="/alertas/regras/nova" element={createProtectedRoute("alert-rule-create", AlertRuleForm, ['isAdmin', 'isOperador'])} />
                  <Route path="/alertas/regras/:id/editar" element={createProtectedRoute("alert-rule-edit", AlertRuleForm, ['isAdmin', 'isOperador'])} />
                  <Route path="/relatorios" element={createProtectedRoute("reports-hub", ReportsHub)} />
                  <Route path="/relatorios/devices" element={createProtectedRoute("devices-report", DevicesReport)} />
                  <Route path="/relatorios/software" element={createProtectedRoute("software-report", SoftwareReport)} />
                  <Route path="/relatorios/user-sessions" element={createProtectedRoute("user-sessions-report", UserSessionsReport)} />
                  <Route path="/relatorios/atividade-usuario" element={createProtectedRoute("user-activity-by-device", UserActivityByDeviceReport)} />
                  <Route path="/relatorios/eventos" element={createProtectedRoute("events-report", EventsReport)} />
                  <Route path="/relatorios/historico-eventos" element={createProtectedRoute("event-history-report", EventHistoryReport)} />
                  <Route path="/relatorios/geolocalizacao" element={createProtectedRoute("geolocation-report", GeolocationReport)} />
                  <Route path="/relatorios/geolocalizacao/:deviceId" element={createProtectedRoute("geolocation-device-history", GeolocationDeviceHistory)} />
                  <Route path="/grupos" element={createProtectedRoute("device-groups", DeviceGroups, ['isAdmin', 'isOperador'])} />
                  <Route path="/tokens-instalacao" element={createProtectedRoute("installation-tokens", InstallationTokens, ['isTenant', 'isStandAlone'])} />
                  <Route path="/busca" element={createProtectedRoute("global-search", GlobalSearch)} />
                  <Route path="/configuracoes" element={createProtectedRoute("settings", Settings)} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
                </BrowserRouter>
                  </TooltipProvider>
                </ThemeSyncProvider>
              </UserPreferencesProvider>
            </AuthProvider>
          </ErrorBoundary>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </AppWrapper>
);

export default App;
